"use client";

import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";

export interface Variant {
  label: string;
  slug: string;
  url?: string;
}

interface VariantSelectProps {
  options: Variant[];
  onSelect?: (variant: Variant) => void;
  defaultSelected?: Variant;
  title?: string;
  modelSlug?: string;
}

const saveSelVariantCookie = (modelSlug: string, variantSlug: string) => {
  document.cookie = `_selVariant=${encodeURIComponent(`${modelSlug}:${variantSlug}`)};path=/;max-age=${60 * 60 * 24 * 30}`;
};

const VariantSelect: React.FC<VariantSelectProps> = ({
  options = [],
  onSelect,
  defaultSelected,
  title,
  modelSlug,
}) => {
  const [selected, setSelected] = useState<Variant>(defaultSelected || options[0]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // Sync with defaultSelected if it changes
  useEffect(() => {
    if (defaultSelected) {
      setSelected(defaultSelected);
    }
  }, [defaultSelected]);

  // Close on outside click (desktop)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const isMobile = window.innerWidth <= 768;

    if (open && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!options || options.length === 0) return null;

  return (
    <>
      {/* INPUT */}
      <div ref={ref} className="relative w-full">
        <button
          onClick={() => setOpen(true)}
          className="w-full h-[56px] px-4 flex items-center justify-between
                     rounded-xl border border-gray-300 bg-white
                     text-[16px] font-medium text-gray-700"
        >
          {selected?.label}
          <svg
            className="w-5 h-5 text-gray-500"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* DESKTOP DROPDOWN */}
        {open && (
          <div
            className="hidden md:block absolute left-0 top-[60px] w-full
                       rounded-xl bg-white border border-gray-200
                       shadow-lg max-h-[230px] overflow-y-auto z-20"
          >
            {options.map((item) => (
              <div
                key={item.slug}
                onClick={() => {
                  setSelected(item);
                  if (onSelect) onSelect(item);
                  if (modelSlug) saveSelVariantCookie(modelSlug, item.slug);
                  if (item.url) router.push(item.url);
                  setOpen(false);
                }}
                className={`px-4 py-3 cursor-pointer text-[15px]
                  ${
                    selected?.slug === item.slug
                      ? "bg-[#008C8B0D] font-semibold"
                      : "hover:bg-[#008C8B0D]"
                  }`}
              >
                {item.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MOBILE BOTTOM SHEET */}
      {open && (
        <div className="md:hidden fixed inset-0 z-[9999] flex flex-col">
          {/* Overlay */}
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />

          {/* Sheet */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[70vh] flex flex-col animate-slide-up">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-4">
              <p className="text-[18px] font-semibold">
                {title || 'Choose Variant'}
              </p>
              <button onClick={() => setOpen(false)} className="text-2xl">
                ✕
              </button>
            </div>

            {/* List */}
            <div className="max-h-[212px] overflow-y-auto">
              {options.map((item) => (
                <div
                  key={item.slug}
                  onClick={() => {
                    setSelected(item);
                    if (onSelect) onSelect(item);
                    if (modelSlug) saveSelVariantCookie(modelSlug, item.slug);
                    if (item.url) router.push(item.url);
                    setOpen(false);
                  }}
                  className={`px-4 py-2 text-[16px] leading-[24px] border-b cursor-pointer border-gray-200 hover:bg-[#008C8B0D]
                    ${
                      selected?.slug === item.slug
                        ? "bg-[#008C8B0D] font-semibold"
                        : ""
                    }`}
                >
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VariantSelect;
